/**
 * SignatureProofGraphLocked.jsx
 * ═══════════════════════════════════════════════════════════════
 * GRANULLO_SIGNATURE_GRAPH_PERMANENT_SOURCE_LOCK
 * ═══════════════════════════════════════════════════════════════
 *
 * LOCKED PRIMITIVE — DO NOT MODIFY THIS FILE
 *
 * Source: commit d07c8fab4 (exact historical recovery)
 * Authority: FOUNDER — NON-NEGOTIABLE
 * Status: PERMANENTLY FROZEN
 *
 * This is the single source of truth for the Signature Proof Graph.
 * All usages (homepage, marketing, exports) MUST import from this file.
 * No inline reimplementations allowed anywhere in the codebase.
 *
 * Frozen geometry:
 * - Trapezoid bar shapes with seeded per-bar wobble
 * - Diagonal hatch fills with pressure rhythm variance
 * - Permanent Marker percentages with angular wobble
 * - Drifting baseline with organic feel
 * - Deep midnight background (#061224)
 *
 * Canonical numbers (LOCKED):
 * +23% Agriculture  — verified seed suppliers
 * +18% Circularity  — no duplicate waste claims
 * +31% Worker Safety — PPE restricted MCCs
 * +15% CBDC Pilot   — merchant whitelist enforced
 *
 * REGRESSION RULES:
 * - No geometry changes
 * - No hatch logic changes
 * - No spacing ratio changes
 * - No headline alignment changes
 * - No background color changes
 * - No animation timing changes
 * - Homepage imports this primitive only
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

/* ── Seeded pseudo-random for deterministic wobble ── */
function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const BARS = [
  { label: 'Agriculture', uplift: 23, height: 155, proof: 'verified seed suppliers', seed: 101 },
  { label: 'Circularity', uplift: 18, height: 125, proof: 'no duplicate waste claims', seed: 202 },
  { label: 'Worker Safety', uplift: 31, height: 195, proof: 'PPE restricted MCCs', seed: 303 },
  { label: 'CBDC Pilot', uplift: 15, height: 105, proof: 'merchant whitelist enforced', seed: 404 },
];

const BASELINE_Y = 340;
const BAR_WIDTH = 85;
const BAR_GAP = 130;
const START_X = 65;

/* ── Generate a wobbly hand-drawn path for a single bar ── */
function buildSketchBar(x, barHeight, seed) {
  const r = seededRandom(seed);
  const topY = BASELINE_Y - barHeight;

  // Per-bar unique imperfections
  const topLeftLean = (r() - 0.5) * 6;
  const topRightLean = (r() - 0.5) * 5;
  const bottomLeftDrift = (r() - 0.5) * 3;
  const bottomRightDrift = (r() - 0.5) * 3;
  const topWobble1 = (r() - 0.5) * 4;
  const topWobble2 = (r() - 0.5) * 4;
  const widthVar = (r() - 0.5) * 6;

  // Four corners with organic variance
  const bl = { x: x + bottomLeftDrift, y: BASELINE_Y + (r() - 0.5) * 2 };
  const tl = { x: x + topLeftLean + 2, y: topY + (r() - 0.5) * 3 };
  const tr = { x: x + BAR_WIDTH + topRightLean + widthVar - 2, y: topY + topWobble1 };
  const br = { x: x + BAR_WIDTH + bottomRightDrift + widthVar, y: BASELINE_Y + (r() - 0.5) * 2 };

  // Mid-edge wobble control points for organic edges
  const leftMidY = (bl.y + tl.y) / 2;
  const rightMidY = (br.y + tr.y) / 2;
  const topMidX = (tl.x + tr.x) / 2;

  const leftWobble = (r() - 0.5) * 5;
  const rightWobble = (r() - 0.5) * 5;

  // Build path with cubic beziers for wobbly edges
  return `
    M ${bl.x} ${bl.y}
    C ${bl.x + leftWobble} ${leftMidY + 20}, ${tl.x - leftWobble * 0.7} ${leftMidY - 20}, ${tl.x} ${tl.y}
    C ${tl.x + 8} ${tl.y + topWobble2}, ${topMidX - 5} ${tl.y - topWobble1 * 0.5}, ${topMidX} ${(tl.y + tr.y) / 2 + topWobble2 * 0.3}
    C ${topMidX + 5} ${tr.y + topWobble1 * 0.4}, ${tr.x - 8} ${tr.y - topWobble2 * 0.6}, ${tr.x} ${tr.y}
    C ${tr.x + rightWobble * 0.7} ${rightMidY - 20}, ${br.x - rightWobble} ${rightMidY + 20}, ${br.x} ${br.y}
    Z
  `;
}

/* ── Generate varied hatch lines for a bar ── */
function buildHatchLines(x, barHeight, seed) {
  const r = seededRandom(seed + 500);
  const topY = BASELINE_Y - barHeight;
  const lines = [];
  const baseSpacing = 7;

  let offset = -BAR_WIDTH * 0.3;
  while (offset < barHeight + BAR_WIDTH * 1.3) {
    // Varied spacing (pressure rhythm)
    const spacing = baseSpacing + (r() - 0.5) * 3;
    offset += spacing;

    // Angle variance (mostly 45° but slightly off)
    const angleVar = (r() - 0.5) * 4;

    // Start/end points for diagonal line
    const startX = x - 5 + (r() - 0.5) * 2;
    const startY = topY + offset;
    const endX = startX + BAR_WIDTH + 10 + (r() - 0.5) * 3;
    const endY = startY - BAR_WIDTH - 10 + angleVar;

    // Opacity variance (pressure rhythm)
    const opacity = 0.12 + r() * 0.18;
    // Stroke width variance
    const sw = 1.2 + r() * 1.2;

    // Slight overrun beyond edges
    const overrun = r() * 3;

    lines.push({
      x1: startX - overrun,
      y1: startY + (r() - 0.5) * 1.5,
      x2: endX + overrun,
      y2: endY + (r() - 0.5) * 1.5,
      opacity,
      sw,
    });
  }
  return lines;
}

/* ── Build a wobbly baseline path ── */
function buildBaselinePath(startX, endX) {
  const r = seededRandom(999);
  const segments = 12;
  const step = (endX - startX) / segments;
  let d = `M ${startX} ${BASELINE_Y + (r() - 0.5) * 2}`;
  for (let i = 1; i <= segments; i++) {
    const px = startX + i * step;
    const py = BASELINE_Y + (r() - 0.5) * 2.5;
    const cpx = startX + (i - 0.5) * step + (r() - 0.5) * 4;
    const cpy = BASELINE_Y + (r() - 0.5) * 3;
    d += ` Q ${cpx} ${cpy}, ${px} ${py}`;
  }
  return d;
}

const HandDrawnBar = ({ x, barHeight, uplift, label, proof, seed, index, delay }) => {
  const barPath = useMemo(() => buildSketchBar(x, barHeight, seed), [x, barHeight, seed]);
  const hatchLines = useMemo(() => buildHatchLines(x, barHeight, seed), [x, barHeight, seed]);

  const topY = BASELINE_Y - barHeight;
  const r = seededRandom(seed + 700);
  // Per-label angular wobble for percentages
  const pctRotation = (r() - 0.5) * 5;
  const pctXDrift = (r() - 0.5) * 6;
  const pctYDrift = (r() - 0.5) * 4;
  const clipId = `bar-clip-${index}`;

  return (
    <g data-testid={`uplift-bar-${label.replace(/\s/g, '-').toLowerCase()}`}>
      {/* Clip path to contain hatching within bar shape */}
      <defs>
        <clipPath id={clipId}>
          <path d={barPath} />
        </clipPath>
      </defs>

      {/* Bar outline — thick white hand-drawn */}
      <motion.path
        d={barPath}
        fill="none"
        stroke="rgba(255,255,255,0.82)"
        strokeWidth="2.8"
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: delay, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Hatch fill — clipped to bar, varied pressure */}
      <g clipPath={`url(#${clipId})`}>
        {hatchLines.map((line, i) => (
          <motion.line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={`rgba(255,255,255,${line.opacity})`}
            strokeWidth={line.sw}
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: delay + 0.3 + i * 0.015 }}
          />
        ))}
      </g>

      {/* Uplift % above bar — Permanent Marker with human wobble */}
      <motion.text
        x={x + BAR_WIDTH / 2 + pctXDrift}
        y={topY - 18 + pctYDrift}
        textAnchor="middle"
        fill="white"
        fontSize="28"
        fontWeight="700"
        fontFamily="'Permanent Marker', cursive"
        transform={`rotate(${pctRotation}, ${x + BAR_WIDTH / 2 + pctXDrift}, ${topY - 18 + pctYDrift})`}
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: delay + 0.6 }}
        className="uplift-pct"
      >
        +{uplift}%
      </motion.text>

      {/* Sector label below baseline */}
      <motion.text
        x={x + BAR_WIDTH / 2}
        y={BASELINE_Y + 28}
        textAnchor="middle"
        fill="rgba(255,255,255,0.7)"
        fontSize="14"
        fontWeight="500"
        fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: delay + 0.7 }}
        className="uplift-label"
      >
        {label}
      </motion.text>

      {/* Causal proof subtext */}
      <motion.text
        x={x + BAR_WIDTH / 2}
        y={BASELINE_Y + 46}
        textAnchor="middle"
        fill="rgba(255,255,255,0.3)"
        fontSize="9"
        fontFamily="monospace"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3, delay: delay + 0.8 }}
      >
        {proof}
      </motion.text>

      {/* Faint dashed guide below label */}
      <motion.line
        x1={x + BAR_WIDTH / 2}
        y1={BASELINE_Y + 52}
        x2={x + BAR_WIDTH / 2}
        y2={BASELINE_Y + 66}
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="1"
        strokeDasharray="2 3"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3, delay: delay + 0.9 }}
      />
    </g>
  );
};

const OutcomeUpliftBars = () => {
  const totalWidth = START_X * 2 + (BARS.length - 1) * BAR_GAP + BAR_WIDTH;
  const baselinePath = useMemo(
    () => buildBaselinePath(START_X - 30, START_X + (BARS.length - 1) * BAR_GAP + BAR_WIDTH + 30),
    []
  );

  return (
    <section
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: '#061224' }}
      data-testid="outcome-uplift-section"
    >
      <div className="max-w-[900px] mx-auto px-6 lg:px-8">
        {/* Headline — full Permanent Marker hand-drawn white */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-[28px] sm:text-[38px] md:text-[44px] leading-[1.1] tracking-[-0.01em]"
            style={{
              color: '#F8FAFC',
              fontFamily: '"Permanent Marker", cursive',
            }}
            data-testid="uplift-headline"
          >
            What happens when money<br />
            reaches its intended destination?
          </h2>
        </motion.div>

        {/* Explanatory paragraph */}
        <motion.p
          className="text-center text-[14px] sm:text-[15px] leading-relaxed max-w-[600px] mx-auto mb-14"
          style={{ color: 'rgba(255,255,255,0.55)' }}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          When misuse is prevented before money moves, outcomes stop being
          theoretical. Crop yields rise. Waste workers earn fairly. Benefits reach
          essentials. CBDCs stay true to public intent.
        </motion.p>

        {/* SVG Canvas with true hand-drawn bars */}
        <div className="flex justify-center">
          <svg
            viewBox={`0 0 ${totalWidth} 400`}
            className="w-full max-w-[720px]"
            preserveAspectRatio="xMidYMid meet"
            data-testid="uplift-bars-svg"
          >
            {/* Wobbly hand-drawn baseline */}
            <motion.path
              d={baselinePath}
              fill="none"
              stroke="rgba(255,255,255,0.5)"
              strokeWidth="2.2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            />

            {/* Bars */}
            {BARS.map((bar, i) => (
              <HandDrawnBar
                key={bar.label}
                x={START_X + i * BAR_GAP}
                barHeight={bar.height}
                uplift={bar.uplift}
                label={bar.label}
                proof={bar.proof}
                seed={bar.seed}
                index={i}
                delay={0.25 + i * 0.18}
              />
            ))}
          </svg>
        </div>
      </div>

      {/* CSS for hover effects */}
      <style>{`
        .uplift-label:hover {
          fill: rgba(255,255,255,0.9) !important;
        }
        @keyframes upliftGlow {
          0%, 100% { filter: drop-shadow(0 0 3px rgba(255,255,255,0.08)); }
          50% { filter: drop-shadow(0 0 8px rgba(255,255,255,0.2)); }
        }
        .uplift-pct {
          animation: upliftGlow 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default OutcomeUpliftBars;
